/**
 * Query Thread Component - CR-1403, CR-2220
 *
 * Chat-style display of query messages between customer and supplier:
 * - Message history with sender info
 * - Reply form with text input
 * - File attachment support
 * - Status indicator (open/responded/closed)
 * - CR-2220: Delete and strikethrough functionality
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Loader2,
  Send,
  MessageSquare,
  Paperclip,
  Clock,
  CheckCircle,
  XCircle,
  User,
  MoreVertical,
  Trash2,
  Strikethrough,
  Undo2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import FileDropZone from '../common/FileDropZone';
import { rfqAPI } from '../../lib/api';
import type { RFQDocument, RFQQuery, RFQQueryMessage } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import {
  MAX_ATTACHMENT_BYTES, formatBytes, uploadQueryAttachments, downloadQueryAttachment,
} from './queryThreadAttachments';

interface QueryThreadProps {
  query: RFQQuery;
  currentUserId: number;
  onRefresh?: () => void;
  /**
   * Whether this viewer may attach a file to a reply. Uploads create an
   * RFQDocument, and `RFQDocumentViewSet` write is customer-only
   * (`RFQCustomerWritePermission`) — so a supplier gets the control disabled
   * with the reason, never a button that fails on click.
   */
  canAttach?: boolean;
}

export default function QueryThread({
  query, currentUserId, onRefresh, canAttach = false,
}: QueryThreadProps) {
  const messages = query.messages || [];
  const [newMessage, setNewMessage] = useState('');
  const { formatDate } = useFormatters();
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getStatusBadge = () => {
    switch (query.status) {
      case 'open':
        return (
          <Badge variant="outline" className="border-blue-500 text-blue-600">
            <Clock className="h-3 w-3 mr-1" />
            Open
          </Badge>
        );
      case 'responded':
        return (
          <Badge variant="outline" className="border-green-500 text-green-600">
            <CheckCircle className="h-3 w-3 mr-1" />
            Responded
          </Badge>
        );
      case 'closed':
        return (
          <Badge variant="secondary">
            <XCircle className="h-3 w-3 mr-1" />
            Closed
          </Badge>
        );
      default:
        return null;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };


  const handleDownload = async (doc: RFQDocument) => {
    try {
      setDownloadingId(doc.id);
      await downloadQueryAttachment(doc);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    } finally {
      setDownloadingId(null);
    }
  };

  // Customer review 11 Sep (drag and drop everywhere): the reply's drop zone
  // enforces MAX_ATTACHMENT_BYTES and names any file it refuses.
  const handleFilesChosen = (chosen: File[]) => {
    setPendingFiles((current) => [...current, ...chosen]);
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((current) => current.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      const attachmentIds = pendingFiles.length ? await uploadQueryAttachments(query.rfq, pendingFiles) : [];
      await rfqAPI.respondToQuery(query.id, newMessage.trim(), attachmentIds);
      setNewMessage('');
      setPendingFiles([]);
      toast.success(attachmentIds.length ? 'Message sent with attachment' : 'Message sent');

      // Refresh to get updated messages
      onRefresh?.();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCloseQuery = async () => {
    try {
      setClosing(true);
      await rfqAPI.closeQuery(query.id);
      toast.success('Query closed');
      onRefresh?.();
    } catch (error) {
      console.error('Error closing query:', error);
      toast.error('Failed to close query');
    } finally {
      setClosing(false);
    }
  };

  // CR-2220: Delete message handler
  const handleDeleteMessage = async (messageId: number) => {
    try {
      setActionLoading(messageId);
      await rfqAPI.deleteQueryMessage(messageId);
      toast.success('Message deleted');
      onRefresh?.();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    } finally {
      setActionLoading(null);
    }
  };

  // CR-2220: Strikethrough message handler
  const handleStrikethroughMessage = async (messageId: number) => {
    try {
      setActionLoading(messageId);
      await rfqAPI.strikethroughQueryMessage(messageId);
      toast.success('Message marked as struck through');
      onRefresh?.();
    } catch (error) {
      console.error('Error striking through message:', error);
      toast.error('Failed to strikethrough message');
    } finally {
      setActionLoading(null);
    }
  };

  // CR-2220: Remove strikethrough handler
  const handleRemoveStrikethrough = async (messageId: number) => {
    try {
      setActionLoading(messageId);
      await rfqAPI.removeStrikethroughQueryMessage(messageId);
      toast.success('Strikethrough removed');
      onRefresh?.();
    } catch (error) {
      console.error('Error removing strikethrough:', error);
      toast.error('Failed to remove strikethrough');
    } finally {
      setActionLoading(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // CR-2220: Render message actions dropdown for current user's messages
  const renderMessageActions = (message: RFQQueryMessage, isCurrentUser: boolean) => {
    if (!isCurrentUser || query.status === 'closed') return null;

    const isLoading = actionLoading === message.id;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <MoreVertical className="h-3 w-3" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {message.is_strikethrough ? (
            <DropdownMenuItem onClick={() => handleRemoveStrikethrough(message.id)}>
              <Undo2 className="h-4 w-4 mr-2" />
              Remove Strikethrough
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => handleStrikethroughMessage(message.id)}>
              <Strikethrough className="h-4 w-4 mr-2" />
              Strikethrough
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => handleDeleteMessage(message.id)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">{query.subject}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge()}
            {query.status !== 'closed' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCloseQuery}
                disabled={closing}
              >
                {closing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  'Close Query'
                )}
              </Button>
            )}
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Started on {formatDate(query.created_at)}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Messages */}
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          {messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No messages yet
            </p>
          ) : (
            messages.map((message) => {
              const senderId = typeof message.sender === 'number' ? message.sender : message.sender;
              const isCurrentUser = senderId === currentUserId;
              const senderName = message.sender_name || 'User';

              return (
                <div
                  key={message.id}
                  className={`flex gap-3 group ${isCurrentUser ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${isCurrentUser ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    <User className="h-4 w-4" />
                  </div>
                  <div
                    className={`flex-1 max-w-[80%] ${
                      isCurrentUser ? 'text-right' : ''
                    }`}
                  >
                    <div className={`flex items-start gap-1 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                      <div
                        className={`rounded-lg p-3 ${
                          isCurrentUser
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        } ${message.is_strikethrough ? 'opacity-60' : ''}`}
                      >
                        <p className={`text-sm whitespace-pre-wrap ${message.is_strikethrough ? 'line-through' : ''}`}>
                          {message.message}
                        </p>
                        {/* Attachments travel with the message; both sides can
                            open them — document READ is company-scoped, only
                            write is customer-only. */}
                        {message.attachments?.length > 0 && (
                          <div className={`mt-2 space-y-1 ${isCurrentUser ? 'text-right' : ''}`}>
                            {message.attachments.map((doc) => (
                              <button
                                key={doc.id}
                                type="button"
                                onClick={() => handleDownload(doc)}
                                disabled={downloadingId === doc.id}
                                className={`flex items-center gap-1 text-xs underline underline-offset-2 disabled:opacity-60 ${
                                  isCurrentUser ? 'flex-row-reverse ml-auto' : ''
                                }`}
                              >
                                {downloadingId === doc.id ? (
                                  <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />
                                ) : (
                                  <Paperclip className="h-3 w-3 flex-shrink-0" />
                                )}
                                <span className="truncate">{doc.file_name || doc.title}</span>
                                {doc.file_size ? (
                                  <span className="opacity-70">({formatBytes(doc.file_size)})</span>
                                ) : null}
                              </button>
                            ))}
                          </div>
                        )}
                        {/* CR-2220: Show strikethrough indicator */}
                        {message.is_strikethrough && (
                          <p className="text-xs mt-1 italic opacity-70">
                            Struck through by {message.strikethrough_by_name}
                          </p>
                        )}
                      </div>
                      {/* CR-2220: Message actions dropdown */}
                      {renderMessageActions(message, isCurrentUser)}
                    </div>
                    <div className={`flex items-center gap-2 mt-1 text-xs text-muted-foreground ${isCurrentUser ? 'justify-end' : ''}`}>
                      {!isCurrentUser && <span>{senderName}</span>}
                      <span>{formatTimestamp(message.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply Form */}
        {query.status !== 'closed' && (
          <div className="border-t pt-4">
            <div className="flex gap-2">
              <Textarea
                placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                className="min-h-[80px] resize-none"
                disabled={sending}
              />
            </div>
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {pendingFiles.map((file, index) => (
                  <span
                    key={`${file.name}-${index}`}
                    className="inline-flex items-center gap-1 rounded-md border bg-muted px-2 py-1 text-xs"
                  >
                    <Paperclip className="h-3 w-3" />
                    <span className="max-w-[200px] truncate">{file.name}</span>
                    <span className="opacity-70">({formatBytes(file.size)})</span>
                    <button
                      type="button"
                      onClick={() => removePendingFile(index)}
                      disabled={sending}
                      aria-label={`Remove ${file.name}`}
                      className="ml-1 opacity-70 hover:opacity-100 disabled:opacity-40"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center gap-3 mt-2">
              {canAttach ? (
                <FileDropZone
                  multiple
                  size="compact"
                  icon={Paperclip}
                  maxSize={MAX_ATTACHMENT_BYTES}
                  disabled={sending}
                  hint={`Attached to this reply — up to ${formatBytes(MAX_ATTACHMENT_BYTES)} each`}
                  onFiles={handleFilesChosen}
                  className="flex-1"
                  data-testid="query-reply-drop-zone"
                />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Only the customer can attach files to a query thread.
                </p>
              )}
              <Button
                onClick={handleSendMessage}
                disabled={sending || !newMessage.trim()}
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {query.status === 'closed' && (
          <div className="border-t pt-4 text-center text-sm text-muted-foreground">
            This query has been closed. No further messages can be added.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
